import Cookie from 'cookie-universal'
import { type ApiError, CartsService } from '@mc/api'
import { DISCOUNT_COOKIE_KEY } from '@mc/constants'
import { trackError } from '@mc/monitoring'
import { CERT_CREDIT_ADD_ON_PRODUCT_TYPE } from './constants'

const report = (error: unknown, context: Record<string, string>) =>
  trackError(error as ApiError, {
    source: 'prcng-1138_v1 addCertsQuizCart',
    ...context,
  })

const addSubscription = (productId: number) =>
  CartsService.addProductToCart({
    requestBody: {
      productId,
      gift: false,
      productType: 'subscription',
      discountSlug: Cookie().get(DISCOUNT_COOKIE_KEY) || undefined,
    },
  })

/**
 * Builds the plans page's membership + certificate cart.
 *
 * ORDERING CONTRACT: the subscription must land BEFORE the certificate. As a
 * guest, `carts_controller` refuses an `add_on_subscription` outright when no
 * subscription line is present; logged in, a subscription add runs
 * `destroy_all`, so adding the certificate first would see it silently evicted.
 *
 * `gift` is hard-coded: the card only renders when `useExperimentCriteria` has
 * already established the visitor is not gifting, so a gift buyer cannot reach
 * this module.
 *
 * Returns whether the certificate is genuinely in the cart, which is what the
 * caller puts on the URL for the payment step. The certificate is optional, so
 * every certificate-side failure degrades to a membership-only purchase rather
 * than blocking the sale — only a missing subscription is fatal.
 */
export const addCertsQuizCart = async ({
  productId,
  certificateProductId,
  skipSubscription = false,
}: {
  productId: number
  certificateProductId?: number
  skipSubscription?: boolean
}): Promise<{ hasCertificate: boolean }> => {
  // `skipSubscription` is for the one shape where re-adding would do harm: the
  // subscription is already in the cart and only the certificate is missing. The
  // add is destructive by design, so it would drop a line we want to keep and
  // churn its coupon for nothing.
  if (!skipSubscription) {
    await addSubscription(productId)
  }

  if (certificateProductId) {
    try {
      await CartsService.addProductToCart({
        requestBody: {
          productId: certificateProductId,
          gift: false,
          // Load-bearing, not cosmetic: omitted, the backend derives the type
          // from the product and a PurchasePlan resolves to `subscription`,
          // which is cart-exclusive and would wipe the membership line.
          productType: CERT_CREDIT_ADD_ON_PRODUCT_TYPE,
          // No discountSlug: the cert credit add-on carries no coupon, and the
          // cookie holds the subscription's promo, which does not apply here.
        },
      })
    } catch (error) {
      // The backend refuses anyone already holding the credit, which is a
      // legitimate outcome rather than a fault — it rejects before mutating,
      // so the subscription line is untouched.
      report(error, {
        step: 'certificate',
        productId: String(productId),
        certificateProductId: String(certificateProductId),
      })
    }
  }

  // Both adds can answer 200 and still leave the wrong cart, so the shape is
  // read back rather than inferred from the responses.
  const { products } = await CartsService.getCartProducts({ gift: false })

  // Not merely any subscription: the payment step charges the `product_id` on
  // the URL, so a different line would disagree with the cart.
  const hasSubscription = products.some(
    (product) => product.isSubscription && product.id === productId,
  )
  const hasCertificate =
    !!certificateProductId &&
    products.some((product) => product.id === certificateProductId)

  if (!hasSubscription) {
    // Reachable: `is_pre_purchase_add_on_purchase?` requires the buyer to have
    // no active subscription, so for a logged-in subscriber the certificate add
    // runs `destroy_all` and takes the membership with it. Re-adding restores
    // the membership-only cart rather than stranding them behind an error that
    // would reproduce on every retry.
    report(new Error('Certificate add evicted the membership line'), {
      step: 'recovery',
      productId: String(productId),
    })
    await addSubscription(productId)

    return { hasCertificate: false }
  }

  return { hasCertificate }
}
